import { FlexCol } from '@/components/ui/flex';
import { FilterOptionList } from '@/components/ui/filter-option-list';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useSidebar } from '@/hooks/useSidebarContext';
import {
  useUpdateContextAssigneesService,
  useUpdateContextStatusService,
  useUpdateDetailService,
} from '@/services/regulatoryWatch';
import { cn } from '@/utils/classUtils';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import { getSourceIconUrl } from '@/utils/source-icon';
import type {
  ApplicabilityStatus,
  GetUpdateDetailResponse,
} from '@ditto/shared';
import {
  Archive,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleDashed,
  CircleX,
  ExternalLink,
  User,
} from 'lucide-react';
import { FC, forwardRef, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserProfileApi } from '@/api/users';
import { useCurrentWorkspaceService } from '@/services/workspaces';
import { regulatoryWatchPaths } from '@/paths';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import {
  ContentPanel,
  UpdateFileContent,
} from '../components/UpdateDetail/ContentPanel';
import { DocumentDetailBackLink } from '../components/DocumentDetailBackLink';
import { LeftInfoPanel } from '../components/UpdateDetail/MetaPanel';
import { DocumentTypeBadge } from '../components/columns';
import { PriorityIcon } from '../components/PriorityIcon';
import { PRIORITY_LABELS, STATUS_LABELS, UPDATE_TYPE_LABELS } from '../labels';

type LocationState = {
  updateIds: string[];
  currentIndex: number;
  backPath?: string;
  backLabel?: string;
} | null;

type UpdateTab = 'overview' | 'document';

const STATUS_ICONS: Record<string, FC<{ className?: string }>> = {
  to_review: CircleDashed,
  applicable: CircleCheck,
  not_applicable: CircleX,
  out_of_scope: Archive,
};

const STATUS_OPTIONS: { value: ApplicabilityStatus; label: string }[] = [
  { value: 'to_review', label: 'To review' },
  { value: 'applicable', label: 'Applicable' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'out_of_scope', label: 'Out of scope' },
];

const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'urgent', label: 'Urgent priority' },
  { value: 'high', label: 'High priority' },
  { value: 'medium', label: 'Medium priority' },
  { value: 'low', label: 'Low priority' },
  { value: 'none', label: 'No priority' },
];

// ── Header badges (status, assignee, priority) ──

const HeaderBadge = forwardRef<
  HTMLButtonElement,
  {
    icon?: React.ReactNode;
    label: string;
    onClick?: () => void;
  }
>(({ icon, label, onClick, ...props }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-900 tracking-[0.18px] hover:bg-neutral-100 transition-colors cursor-pointer"
    {...props}
  >
    {icon}
    {label}
  </button>
));

// ── Status dropdown badge ──

const StatusBadgeDropdown: FC<{
  status: string;
  onStatusChange: (status: ApplicabilityStatus) => void;
}> = ({ status, onStatusChange }) => {
  const [open, setOpen] = useState(false);
  const StatusIcon = STATUS_ICONS[status] ?? CircleDashed;
  const label = STATUS_LABELS[status] ?? status;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderBadge
          icon={<StatusIcon className="size-[14px] text-neutral-400" />}
          label={label}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[180px] p-0"
        align="end"
        sideOffset={4}
      >
        <div className="py-1">
          {STATUS_OPTIONS.map(opt => {
            const Icon = STATUS_ICONS[opt.value] ?? CircleDashed;
            const isActive = status === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  onStatusChange(opt.value);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:bg-accent"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Icon className="size-4 shrink-0 text-neutral-400" />
                  <span className="truncate">{opt.label}</span>
                </span>
                {isActive ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── Assignee dropdown badge ──

const AssigneeBadgeDropdown: FC<{
  assignedMemberIds: string[];
  onAssign: (memberIds: string[]) => void;
}> = ({ assignedMemberIds, onAssign }) => {
  const [open, setOpen] = useState(false);
  const { currentWorkspace } = useCurrentWorkspaceService();
  const members = currentWorkspace?.members ?? [];
  const assignedMembers = members.filter(m => assignedMemberIds.includes(m.id));

  const label = assignedMembers.length
    ? assignedMembers
        .map(m => `${m.firstName} ${m.lastName}`.trim() || m.email)
        .join(', ')
    : 'Unassigned';

  const memberOptions = members.map(m => ({
    label: `${m.firstName} ${m.lastName}`.trim() || m.email,
    value: m.id,
  }));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderBadge
          icon={<User className="size-[14px] text-neutral-400" />}
          label={label}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[200px] p-0"
        align="end"
        sideOffset={4}
      >
        <FilterOptionList
          options={memberOptions}
          selected={assignedMemberIds}
          onChange={onAssign}
          showSearch={members.length > 5}
        />
      </PopoverContent>
    </Popover>
  );
};

// ── Priority dropdown badge ──

const PriorityBadgeDropdown: FC<{
  priority: string | null;
  onPriorityChange: (priority: string) => void;
}> = ({ priority, onPriorityChange }) => {
  const [open, setOpen] = useState(false);
  const effectivePriority = priority ?? 'none';
  const label = PRIORITY_LABELS[effectivePriority] ?? 'No priority';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderBadge
          icon={
            <PriorityIcon
              priority={effectivePriority}
              className="size-[14px]"
            />
          }
          label={label}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[180px] p-0"
        align="end"
        sideOffset={4}
      >
        <div className="py-1">
          {PRIORITY_OPTIONS.map(opt => {
            const isActive = effectivePriority === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  onPriorityChange(opt.value);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:bg-accent"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <PriorityIcon
                    priority={opt.value}
                    className="size-4 shrink-0"
                  />
                  <span className="truncate">{opt.label}</span>
                </span>
                {isActive ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── Update type badge (colored pill) ──

const UPDATE_TYPE_PILL_BG: Record<string, string> = {
  new: 'bg-green-100',
  revision: 'bg-yellow-100',
  withdrawal: 'bg-red-100',
  status_change: 'bg-blue-100',
};

const UpdateTypePill: FC<{ updateType: string }> = ({ updateType }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;
  const bg = UPDATE_TYPE_PILL_BG[updateType] ?? 'bg-purple-100';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 tracking-[0.18px]',
        bg
      )}
    >
      {label}
    </span>
  );
};

// ── Reference link button ──

const ReferenceLinkButton: FC<{
  detail: GetUpdateDetailResponse;
  workspaceUname: string;
}> = ({ detail, workspaceUname }) => {
  const ref = detail.reference;
  if (!ref) return null;

  const basePath = `/workspace/${workspaceUname}/regulatory-watch`;
  const sourceIcon = getSourceIconUrl(ref.source.id, ref.source.icon);
  const flag = getGeographyFlag(ref.source.geography);

  return (
    <Link
      to={`${basePath}/references/${ref.id}`}
      className="inline-flex items-center gap-1.5 min-w-0 px-4 py-2 rounded-lg border border-neutral-200 bg-white shadow-sm hover:border-neutral-300 transition-colors"
    >
      <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-600 shrink-0">
        Reference
      </span>
      <span className="min-w-0 text-sm font-medium text-neutral-900 truncate tracking-[0.07px]">
        {ref.title}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        {ref.type && <DocumentTypeBadge type={ref.type} />}
        {flag && (
          <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-900 tracking-[0.18px]">
            <img
              src={flag}
              alt=""
              className="size-[14px] shrink-0 object-contain"
            />
            {getGeographyLabel(ref.source.geography)}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-900 tracking-[0.18px]">
          {sourceIcon && (
            <img
              src={sourceIcon}
              alt=""
              className="size-[14px] shrink-0 object-contain rounded-full border border-neutral-200"
            />
          )}
          {ref.source.title}
        </span>
      </div>
      <ArrowRight className="size-4 shrink-0 text-neutral-400" />
    </Link>
  );
};

// ── Overview tab content (reuses the existing LeftInfoPanel) ──

// ── Supporting document tab content ──

/** Determine the second tab's title and content mode based on update data. */
function getDocumentTabInfo(detail: GetUpdateDetailResponse): {
  label: string | React.ReactNode;
  mode: 'redline' | 'update-file' | 'latest-version' | 'news';
} {
  if (detail.kind === 'news') {
    return { label: 'Content', mode: 'news' };
  }
  const hasRedline =
    detail.redline?.status === 'completed' && !!detail.redline?.excelFileUrl;
  if (hasRedline) {
    return { label: 'Redline', mode: 'redline' };
  }
  if (detail.update.sharedFile) {
    return { label: 'Update Document', mode: 'update-file' };
  }
  return { label: 'Latest version Document', mode: 'latest-version' };
}

const DocumentTab: FC<{
  detail: GetUpdateDetailResponse;
}> = ({ detail }) => {
  const { mode } = getDocumentTabInfo(detail);

  if (mode === 'news') {
    const hasContent = !!detail.update.content;
    const hasFile = !!detail.update.sharedFile;

    if (hasContent && hasFile) {
      // Side-by-side: markdown left, file right
      return (
        <div className="flex-1 min-h-0 flex">
          <div className="w-1/2 overflow-y-auto p-6">
            <MarkdownText size="sm">{detail.update.content!}</MarkdownText>
          </div>
          <div className="w-1/2 min-h-0">
            <UpdateFileContent fileUrl={detail.update.sharedFile!.signedUrl} />
          </div>
        </div>
      );
    }
    if (hasContent) {
      return (
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <MarkdownText size="sm">{detail.update.content!}</MarkdownText>
        </div>
      );
    }
    if (hasFile) {
      return (
        <div className="flex-1 min-h-0">
          <div className="h-full">
            <UpdateFileContent fileUrl={detail.update.sharedFile!.signedUrl} />
          </div>
        </div>
      );
    }
    return (
      <FlexCol
        alignItems="center"
        justifyContent="center"
        className="flex-1 min-h-0"
        gap="3"
      >
        <span className="text-sm text-neutral-500">
          No content available for this news item.
        </span>
      </FlexCol>
    );
  }

  // Regulatory update modes
  return (
    <div className="flex-1 min-h-0 p-6">
      <div className="h-full bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {mode === 'redline' ? (
          <ContentPanel
            activeTab="redline"
            currentVersion={detail.currentVersion}
            previousVersion={detail.previousVersion}
            redline={detail.redline}
          />
        ) : mode === 'update-file' && detail.update.sharedFile ? (
          <UpdateFileContent fileUrl={detail.update.sharedFile.signedUrl} />
        ) : (
          <ContentPanel
            activeTab="latest"
            currentVersion={detail.currentVersion}
            previousVersion={detail.previousVersion}
            redline={detail.redline}
          />
        )}
      </div>
    </div>
  );
};

// ── Main page ──

export const UpdateDetailPage: FC = () => {
  const { updateId, workspaceUname } = useParams<{
    updateId: string;
    workspaceUname: string;
  }>();
  const navigate = useNavigate();
  const basePath = `/workspace/${workspaceUname}/regulatory-watch`;
  const location = useLocation();

  const locationState = location.state as LocationState;
  const updateIds = locationState?.updateIds;
  const currentIndex = locationState?.currentIndex;
  const backPath =
    locationState?.backPath ?? `${basePath}/${regulatoryWatchPaths.newsfeed}`;
  const backLabel = locationState?.backLabel ?? 'All regulatory updates';

  const { setIsCollapsed } = useSidebar();

  useEffect(() => {
    setIsCollapsed(true);
  }, [setIsCollapsed]);

  const { detail, isPending } = useUpdateDetailService(updateId);
  const { data: userProfile } = useUserProfileApi();
  const isDittoUser = userProfile?.type === 'ditto';
  const { updateStatus, updatePriority } = useUpdateContextStatusService();
  const { updateAssignees } = useUpdateContextAssigneesService();

  const [activeTab, setActiveTab] = useState<UpdateTab>('overview');

  const hasPagination =
    updateIds !== undefined &&
    currentIndex !== undefined &&
    updateIds.length > 0;

  const handleBack = () => {
    navigate(`${backPath}${location.search}`);
  };

  const handlePrev = () => {
    if (!hasPagination || currentIndex <= 0) return;
    const prevIndex = currentIndex - 1;
    const prevId = updateIds[prevIndex];
    navigate(`${basePath}/updates/${prevId}${location.search}`, {
      state: {
        updateIds,
        currentIndex: prevIndex,
        backPath: locationState?.backPath,
        backLabel: locationState?.backLabel,
      },
    });
  };

  const handleNext = () => {
    if (!hasPagination || currentIndex >= updateIds.length - 1) return;
    const nextIndex = currentIndex + 1;
    const nextId = updateIds[nextIndex];
    navigate(`${basePath}/updates/${nextId}${location.search}`, {
      state: {
        updateIds,
        currentIndex: nextIndex,
        backPath: locationState?.backPath,
        backLabel: locationState?.backLabel,
      },
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBack();
      if (e.key === 'ArrowUp' && hasPagination) {
        e.preventDefault();
        handlePrev();
      }
      if (e.key === 'ArrowDown' && hasPagination) {
        e.preventDefault();
        handleNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasPagination, currentIndex, updateIds, location.search]);

  const documentTabInfo = detail ? getDocumentTabInfo(detail) : null;

  const effectiveDate = detail?.currentVersion?.effectiveDate ?? null;

  const breadcrumbTitle = isPending
    ? '...'
    : detail
      ? (detail.update.shortSummary ?? detail.update.title ?? 'Update')
      : 'Update detail';

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Top breadcrumb bar */}
      <div className="h-14 border-b border-neutral-200 flex items-center justify-between px-6 bg-white shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <DocumentDetailBackLink label={backLabel} onClick={handleBack} />
          <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
          <span className="text-sm font-medium text-neutral-900 truncate">
            {breadcrumbTitle}
          </span>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {isDittoUser && updateId && (
            <Link
              to={`/admin/regulatory-watch/updates/${updateId}`}
              className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              See in back office
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
          {hasPagination && (
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-neutral-500 tabular-nums">
                {currentIndex + 1} / {updateIds.length}
              </span>
              <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className="h-7 w-7 flex items-center justify-center rounded-md border border-neutral-200 hover:bg-neutral-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                onClick={handleNext}
                disabled={currentIndex === updateIds.length - 1}
                className="h-7 w-7 flex items-center justify-center rounded-md border border-neutral-200 hover:bg-neutral-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0">
        {isPending ? (
          <div className="p-6 flex flex-col gap-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-full max-w-3xl" />
            <Skeleton className="h-4 w-full max-w-2xl" />
            <Skeleton className="h-[400px] w-full mt-4" />
          </div>
        ) : detail ? (
          <>
            {/* Header section */}
            <div className="shrink-0 bg-white border-b border-neutral-200 pt-6 px-6 flex flex-col gap-4">
              <div className="flex flex-col gap-2.5">
                {/* Top block: UPDATE label + badges, title + dates */}
                <div className="flex flex-col gap-2">
                  {/* Row 1: UPDATE label + badges */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-600 flex-1">
                      UPDATE
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {detail.context && (
                        <>
                          <StatusBadgeDropdown
                            status={detail.context.status}
                            onStatusChange={status =>
                              updateStatus(detail.context!.id, status)
                            }
                          />
                          <AssigneeBadgeDropdown
                            assignedMemberIds={
                              detail.context.assignedMemberIds ?? []
                            }
                            onAssign={memberIds =>
                              updateAssignees({
                                id: detail.context!.id,
                                memberIds,
                              })
                            }
                          />
                          <PriorityBadgeDropdown
                            priority={detail.context.priority}
                            onPriorityChange={priority =>
                              updatePriority(
                                detail.context!.id,
                                priority as any
                              )
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Title + dates */}
                  <div className="flex flex-col gap-1">
                    <h1 className="text-base font-medium text-neutral-900 leading-[1.4]">
                      {detail.update.shortSummary ??
                        detail.update.title ??
                        'Update'}
                    </h1>
                    <div className="flex items-center gap-4">
                      <p className="text-xs tracking-[0.18px]">
                        <span className="italic text-neutral-700">
                          Published:
                        </span>{' '}
                        <span className="font-medium italic text-black">
                          {new Date(
                            detail.update.publicationDate
                          ).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </span>
                      </p>
                      {effectiveDate && (
                        <p className="text-xs tracking-[0.18px]">
                          <span className="italic text-neutral-700">
                            Effective:
                          </span>{' '}
                          <span className="font-medium italic text-black">
                            {new Date(effectiveDate).toLocaleDateString(
                              'en-GB',
                              {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        </p>
                      )}
                      {(detail.articleUrl ||
                        detail.currentVersion?.url ||
                        detail.reference?.url) && (
                        <a
                          href={
                            detail.articleUrl ??
                            detail.currentVersion?.url ??
                            detail.reference?.url ??
                            '#'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          View original
                          <ExternalLink className="size-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 3: Type badge + reference link */}
                <div className="flex items-center gap-1.5">
                  {detail.update.updateType && (
                    <UpdateTypePill updateType={detail.update.updateType} />
                  )}
                  {detail.reference && (
                    <>
                      {detail.update.updateType !== 'new' && (
                        <span className="text-sm text-neutral-700 tracking-[0.07px]">
                          {detail.update.updateType === 'withdrawal'
                            ? 'of:'
                            : 'to:'}
                        </span>
                      )}
                      <ReferenceLinkButton
                        detail={detail}
                        workspaceUname={workspaceUname ?? ''}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 -mb-px">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={cn(
                    'px-6 h-[52px] flex items-center justify-center rounded-t-lg border text-base transition-colors',
                    activeTab === 'overview'
                      ? 'bg-white border-neutral-200 border-b-white font-medium text-black relative z-10'
                      : 'bg-neutral-50 border-neutral-200 border-b-neutral-200 text-neutral-500 hover:text-neutral-700'
                  )}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('document')}
                  className={cn(
                    'px-6 h-[52px] flex items-center justify-center rounded-t-lg border text-base transition-colors',
                    activeTab === 'document'
                      ? 'bg-white border-neutral-200 border-b-white font-medium text-black relative z-10'
                      : 'bg-neutral-50 border-neutral-200 border-b-neutral-200 text-neutral-500 hover:text-neutral-700'
                  )}
                >
                  {documentTabInfo?.label ?? 'Document'}
                </button>
              </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 min-h-0 flex flex-col">
              {activeTab === 'overview' ? (
                <LeftInfoPanel detail={detail} hideBadgesAndTitle />
              ) : (
                <DocumentTab detail={detail} />
              )}
            </div>
          </>
        ) : (
          <FlexCol
            alignItems="center"
            justifyContent="center"
            className="h-full"
            gap="3"
          >
            <span className="text-sm text-neutral-500">Update not found</span>
          </FlexCol>
        )}
      </div>
    </div>
  );
};
